import { z } from 'zod';

// Incidents with a named owner (setter spec §05.2e, §05.19, §05.20, UAT-28). An advisor reports «no puedo atender esta
// cita» (or a member opens another kind of incident); the case goes to ONE named person — the project's «responsable de
// incidencias» — who takes it, picks a resolution and LIA applies it through the existing flows. LIA proposes options
// but never reassigns or changes an appointment on its own. Not taken in time → backup owner, then workspace admins.

const Id = z.string().min(1).max(64);
const Iso = z.string();

export const INCIDENT_KINDS = ['advisor_unavailable', 'calendar_conflict', 'crm_failure', 'data_conflict', 'other'] as const;
export const IncidentKindSchema = z.enum(INCIDENT_KINDS);
export type IncidentKind = z.infer<typeof IncidentKindSchema>;
export const INCIDENT_KIND_LABELS: Record<IncidentKind, string> = {
  advisor_unavailable: 'Asesor no puede atender la cita',
  calendar_conflict: 'Conflicto de calendario',
  crm_failure: 'Falla del CRM',
  data_conflict: 'Datos en conflicto',
  other: 'Otra incidencia',
};

export const INCIDENT_STATES = ['open', 'acknowledged', 'escalated', 'resolved'] as const;
export const IncidentStateSchema = z.enum(INCIDENT_STATES);
export type IncidentState = z.infer<typeof IncidentStateSchema>;
export const INCIDENT_STATE_LABELS: Record<IncidentState, string> = {
  open: 'Abierta',
  acknowledged: 'Tomada',
  escalated: 'Escalada',
  resolved: 'Resuelta',
};

export const INCIDENT_RESOLUTIONS = ['replace_advisor', 'keep_time', 'reschedule', 'other'] as const;
export const IncidentResolutionSchema = z.enum(INCIDENT_RESOLUTIONS);
export type IncidentResolution = z.infer<typeof IncidentResolutionSchema>;
export const INCIDENT_RESOLUTION_LABELS: Record<IncidentResolution, string> = {
  replace_advisor: 'Reemplazar al asesor',
  keep_time: 'Conservar el horario',
  reschedule: 'Reprogramar la cita',
  other: 'Otra solución',
};

export const INCIDENT_OWNER_SOURCES = ['incident_owner', 'backup', 'admin', 'manager_fallback', 'manual', 'unassigned'] as const;
export const IncidentOwnerSourceSchema = z.enum(INCIDENT_OWNER_SOURCES);
export type IncidentOwnerSource = z.infer<typeof IncidentOwnerSourceSchema>;
export const INCIDENT_OWNER_SOURCE_LABELS: Record<IncidentOwnerSource, string> = {
  incident_owner: 'Responsable de incidencias del proyecto',
  backup: 'Responsable suplente (escalamiento)',
  admin: 'Administración del espacio (escalamiento)',
  manager_fallback: 'Gerencia del proyecto (sin responsable designado)',
  manual: 'Asignada manualmente',
  unassigned: 'Sin responsable disponible',
};

/** Values the incident WhatsApp template can receive (essentials + a secure link; no budget or sensitive data). */
export const INCIDENT_NOTICE_FIELDS = ['owner_name', 'project_name', 'incident_kind', 'contact_name', 'appointment_date', 'appointment_time', 'incident_link'] as const;
export const IncidentNoticeFieldSchema = z.enum(INCIDENT_NOTICE_FIELDS);
export const INCIDENT_NOTICE_FIELD_LABELS: Record<z.infer<typeof IncidentNoticeFieldSchema>, string> = {
  owner_name: 'Nombre del responsable',
  project_name: 'Proyecto',
  incident_kind: 'Tipo de incidencia',
  contact_name: 'Nombre del prospecto',
  appointment_date: 'Fecha de la cita',
  appointment_time: 'Hora de la cita',
  incident_link: 'Enlace seguro a la incidencia en LIA',
};

/** Per-project incident policy (§05.20). Defaults: spec numbers (30 min to take the case; notify in LIA + WhatsApp + CRM). */
export const IncidentConfigSchema = z.object({
  /** The named «responsable de incidencias» (a member with access to the project). null = not designated yet. */
  ownerMembershipId: Id.nullable().default(null),
  /** Second person the case escalates to when the owner does not take it in time. */
  backupMembershipId: Id.nullable().default(null),
  /** Minutes to take (acknowledge) the case before it escalates. */
  ackMinutes: z.number().int().min(5).max(1440).default(30),
  /** Critical alerts (v1 §10.9): minutes to take the case from the official-channel notice — 5 by default, the project may shorten it. */
  criticalTakeMinutes: z.number().int().min(1).max(5).default(5),
  notifyWhatsapp: z.boolean().default(true),
  notifyCrm: z.boolean().default(true),
  /** Approved WhatsApp template; required outside the owner's 24 h window. */
  whatsappTemplate: z
    .object({
      name: z.string().min(1).max(200),
      language: z.string().min(2).max(20),
      variables: z.record(z.string().max(40), IncidentNoticeFieldSchema).default({}),
    })
    .nullable()
    .default(null),
});
export type IncidentConfig = z.infer<typeof IncidentConfigSchema>;

export const IncidentMemberSchema = z.object({
  membershipId: Id,
  name: z.string(),
  role: z.string(),
  /** A WhatsApp number is registered in Equipo (notices can be attempted). */
  hasWhatsapp: z.boolean(),
  /** CRM owner this member is mapped to (CRM mapping «responsables»), when any (§05.20 «vinculado a HubSpot»). */
  crmOwner: z.object({ provider: z.string(), externalOwnerId: z.string() }).nullable(),
});
export type IncidentMember = z.infer<typeof IncidentMemberSchema>;

export const IncidentConfigResponseSchema = z.object({
  projectId: Id,
  config: IncidentConfigSchema,
  /** false = never saved: spec defaults, no owner designated. */
  customized: z.boolean(),
  owner: IncidentMemberSchema.nullable(),
  backup: IncidentMemberSchema.nullable(),
  /** Members that can be designated (access to the project, not viewers). */
  eligible: z.array(IncidentMemberSchema),
  /** Honest gaps: no owner, no WhatsApp number, no CRM owner mapping, … */
  warnings: z.array(z.string()),
});
export type IncidentConfigResponse = z.infer<typeof IncidentConfigResponseSchema>;

export const IncidentSchema = z.object({
  id: Id,
  projectId: Id,
  projectName: z.string(),
  kind: IncidentKindSchema,
  state: IncidentStateSchema,
  reason: z.string(),
  ownerMembershipId: Id.nullable(),
  ownerName: z.string().nullable(),
  ownerSource: IncidentOwnerSourceSchema,
  escalationLevel: z.number().int(),
  /** Deadline to take the case; null once taken/resolved. */
  ackDueAt: Iso.nullable(),
  /** Past the deadline and nobody left to escalate to. */
  escalationExhausted: z.boolean(),
  /** Critical alert (v1 §10.9): told to the official channel at once; ≤ 5 min to take it. */
  critical: z.boolean(),
  contactId: Id.nullable(),
  contactName: z.string().nullable(),
  leadInterestId: Id.nullable(),
  appointmentId: Id.nullable(),
  appointmentStartAt: Iso.nullable(),
  appointmentTimezone: z.string().nullable(),
  originalAdvisorName: z.string().nullable(),
  reportedByName: z.string().nullable(),
  acknowledgedAt: Iso.nullable(),
  resolution: IncidentResolutionSchema.nullable(),
  resolutionNote: z.string().nullable(),
  resolvedAt: Iso.nullable(),
  resolvedByName: z.string().nullable(),
  createdAt: Iso,
});
export type Incident = z.infer<typeof IncidentSchema>;

export const IncidentCandidateSchema = z.object({
  membershipId: Id,
  name: z.string(),
  /** Matches a Calendly host name of the project's event types: yes; otherwise availability is unknown. */
  calendlyHost: z.enum(['yes', 'unknown']),
  hasWhatsapp: z.boolean(),
});
export type IncidentCandidate = z.infer<typeof IncidentCandidateSchema>;

const Option = z.object({ available: z.boolean(), detail: z.string() });
/** Options LIA computes for the owner (§05.19 «presentar opciones»). Nothing happens until an authorized human picks one. */
export const IncidentOptionsSchema = z.object({
  replaceAdvisor: Option.extend({ candidates: z.array(IncidentCandidateSchema) }),
  keepTime: Option,
  reschedule: Option.extend({ mode: z.enum(['link', 'advisor_task']).nullable() }),
});
export type IncidentOptions = z.infer<typeof IncidentOptionsSchema>;

export const IncidentDetailSchema = IncidentSchema.extend({
  options: IncidentOptionsSchema,
  history: z.array(z.object({ at: Iso, action: z.string(), label: z.string(), detail: z.string().nullable() })),
  /** What the caller may do (the API re-checks). */
  can: z.object({ acknowledge: z.boolean(), resolve: z.boolean(), escalate: z.boolean(), reassign: z.boolean() }),
  /** Members a manager can reassign the case to. */
  assignable: z.array(IncidentMemberSchema),
  warnings: z.array(z.string()),
});
export type IncidentDetail = z.infer<typeof IncidentDetailSchema>;

export const IncidentListQuerySchema = z.object({
  /** mine = owned or reported by me; all = everything I can see (manager+). */
  scope: z.enum(['mine', 'all']).default('mine'),
  /** active = open / acknowledged / escalated. */
  state: z.enum(['active', 'resolved', 'any']).default('active'),
  projectId: Id.optional(),
});
export type IncidentListQuery = z.infer<typeof IncidentListQuerySchema>;

export const ReportAdvisorUnavailableSchema = z.object({ reason: z.string().trim().min(3).max(1000) });
export type ReportAdvisorUnavailable = z.infer<typeof ReportAdvisorUnavailableSchema>;

export const CreateIncidentSchema = z.object({
  projectId: Id,
  kind: IncidentKindSchema,
  reason: z.string().trim().min(3).max(1000),
  contactId: Id.optional(),
  appointmentId: Id.optional(),
  /** Critical alert: official channel + the project's critical take deadline. Default: an appointment within 24 h. */
  critical: z.boolean().optional(),
});
export type CreateIncident = z.infer<typeof CreateIncidentSchema>;

export const ResolveIncidentSchema = z.object({
  resolution: IncidentResolutionSchema,
  /** Required for replace_advisor: one of the computed candidates. */
  replacementMembershipId: Id.optional(),
  /** The decision as communicated (required for «other»). */
  note: z.string().trim().max(1000).optional(),
});
export type ResolveIncident = z.infer<typeof ResolveIncidentSchema>;

export const EscalateIncidentSchema = z.object({ reason: z.string().trim().min(3).max(500) });
export const ReassignIncidentSchema = z.object({ membershipId: Id, reason: z.string().trim().max(500).optional() });

export const ResolveIncidentResponseSchema = z.object({
  incident: IncidentDetailSchema,
  /** reschedule: what the existing reschedule flow returned. */
  reschedule: z.object({ mode: z.enum(['link', 'advisor_task']), rescheduleUrl: z.string().nullable() }).nullable(),
});
export type ResolveIncidentResponse = z.infer<typeof ResolveIncidentResponseSchema>;
